import { taskAPI } from '@/utils/api';
import { buildHistoryTasksListQuery } from './historyTasksListQuery';
import { parseGoodsCompositeSelectionId } from '@/utils/goodsCompositeSelection';
import { loadGoodsFormsWithCompositeImages } from '@/pages/ipe/workbench/utils/goodsDetailCompositeMerge';

function taskFinishedAtMs(task) {
  const raw = task?.finishedAt || task?.updatedAt || task?.createdAt || '';
  const ms = Date.parse(String(raw));
  return Number.isFinite(ms) ? ms : 0;
}

function isSuccessLikeStatus(status) {
  const s = String(status || '').toLowerCase();
  return s === 'success' || s === 'partial' || s === 'completed' || s === 'done' || s === 'processing';
}

export function resolveGoodsTaskBillingNavForExport(task) {
  const fromTask = task?.billingNavId || task?.result?.billingNavId || task?.result?.sourceNav || '';
  if (fromTask === 'podProductDesign') return 'podProductDesign';
  return 'goods';
}

export function collectLinkedGoodsTaskIdsFromTask(task) {
  const result = task?.result && typeof task.result === 'object' ? task.result : {};
  const ids = [];
  const push = (value) => {
    const id = String(value || '').trim();
    if (id) ids.push(id);
  };
  (Array.isArray(result.linkedGoodsTaskIds) ? result.linkedGoodsTaskIds : []).forEach(push);
  push(result.sourceGoodsTaskId);
  push(result.sourcePodTaskId);
  return [...new Set(ids)];
}

/**
 * 导出行关联键：同源印花/成品。
 * 优先 composite-*（POD 勾选 id / AI 印花引用的上游 id），保证两侧导出合并到同一行。
 */
export function resolveExportPreviewRowLinkKey(row) {
  if (!row || typeof row !== 'object') return '';
  const candidates = [
    row.stampImageId,
    row.stampImages?.[0]?.imageId,
    ...(Array.isArray(row.compositeIds) ? row.compositeIds : []),
  ]
    .map((value) => String(value || '').trim())
    .filter(Boolean);

  const compositeKey = candidates.find((id) => Boolean(parseGoodsCompositeSelectionId(id)));
  if (compositeKey) return compositeKey;
  if (candidates[0]) return candidates[0];
  return String(row.id || '').trim();
}

function dedupeImagesByUrl(images = []) {
  const seen = new Set();
  const out = [];
  (Array.isArray(images) ? images : []).forEach((img) => {
    const url = String(img?.url || '').trim();
    if (!url || seen.has(url)) return;
    seen.add(url);
    out.push(img);
  });
  return out;
}

/**
 * 合并同源预览行：主图创作 → 主图列，POD 商品设计 → 详情图列。
 * 若合并后仍无主图，则用详情图（POD）第一张顶上为主图，详情列仍保留。
 */
export function ensureMainImagesFromDetailFallback(row) {
  if (!row || typeof row !== 'object') return row;
  const mainImages = dedupeImagesByUrl(row.mainImages);
  const detailImages = dedupeImagesByUrl(row.detailImages);
  if (mainImages.length > 0) {
    const resultImages = dedupeImagesByUrl([...mainImages, ...detailImages]);
    return {
      ...row,
      mainImages,
      detailImages,
      resultImages,
      compositeImages: resultImages,
    };
  }
  if (detailImages.length === 0) {
    return {
      ...row,
      mainImages,
      detailImages,
      resultImages: [],
      compositeImages: [],
    };
  }
  const promoted = detailImages[0];
  const nextMain = [promoted];
  const resultImages = dedupeImagesByUrl([...nextMain, ...detailImages]);
  return {
    ...row,
    mainImages: nextMain,
    detailImages,
    resultImages,
    compositeImages: resultImages,
  };
}

/**
 * 合并同源预览行：主图/详情图取并集，预览与导出共用同一结构。
 */
export function mergeExportPreviewRowsByLinkKey(rowLists = []) {
  const merged = new Map();
  const orphans = [];

  rowLists.flat().forEach((row) => {
    if (!row) return;
    const key = resolveExportPreviewRowLinkKey(row);
    if (!key) {
      orphans.push(row);
      return;
    }
    const existing = merged.get(key);
    if (!existing) {
      merged.set(key, {
        ...row,
        id: `export-linked-${key}`,
        mainImages: dedupeImagesByUrl(row.mainImages),
        detailImages: dedupeImagesByUrl(row.detailImages),
        compositeImages: dedupeImagesByUrl([
          ...(row.mainImages || []),
          ...(row.detailImages || []),
          ...(row.compositeImages || []),
        ]),
        resultImages: dedupeImagesByUrl([
          ...(row.resultImages || []),
          ...(row.mainImages || []),
          ...(row.detailImages || []),
        ]),
        stampImages: Array.isArray(row.stampImages) ? [...row.stampImages] : [],
        originalImages: dedupeImagesByUrl(row.originalImages),
        compositeIds: Array.isArray(row.compositeIds) ? [...row.compositeIds] : [],
      });
      return;
    }

    const mainImages = dedupeImagesByUrl([
      ...(existing.mainImages || []),
      ...(row.mainImages || []),
    ]);
    const detailImages = dedupeImagesByUrl([
      ...(existing.detailImages || []),
      ...(row.detailImages || []),
    ]);
    const stampImages = (existing.stampImages?.length || 0) >= (row.stampImages?.length || 0)
      ? existing.stampImages
      : (row.stampImages || existing.stampImages || []);
    const originalImages = dedupeImagesByUrl([
      ...(existing.originalImages || []),
      ...(row.originalImages || []),
    ]);
    const title = existing.title || row.title || null;
    const titleLanguage = existing.titleLanguage || row.titleLanguage || null;
    const displayName = title || '无标题';
    const stampName = existing.stampName || row.stampName || '';
    const stampImageUrl = existing.stampImageUrl || row.stampImageUrl || null;
    const stampImageId = existing.stampImageId || row.stampImageId || '';
    const resultImages = dedupeImagesByUrl([...mainImages, ...detailImages]);
    const titleRole = (existing.titleRole?.title || existing.title)
      ? (existing.titleRole || null)
      : (row.titleRole || null);

    merged.set(key, {
      ...existing,
      ...row,
      id: existing.id,
      title,
      titleLanguage,
      displayName,
      titleRole: titleRole || existing.titleRole || row.titleRole || undefined,
      stampName,
      layerStampName: existing.layerStampName || row.layerStampName || '',
      stampImageId,
      stampImageUrl,
      stampImages,
      originalImages,
      mainImages,
      detailImages,
      resultImages,
      compositeImages: [...mainImages, ...detailImages],
      compositeIds: [...new Set([
        ...(existing.compositeIds || []),
        ...(row.compositeIds || []),
      ])],
    });
  });

  return [
    ...merged.values(),
    ...orphans,
  ].map((row) => ensureMainImagesFromDetailFallback(row));
}

async function ensureFullGoodsTask(task) {
  const forms = task?.goodsForms || task?.result?.goodsForms;
  if (Array.isArray(forms) && forms.length > 0) return task;
  try {
    const res = await taskAPI.getById(task.id);
    return res?.task || res?.data || res || task;
  } catch {
    return task;
  }
}

/** 仅认显式任务 ID 关联（禁止用 composite-* 交集，该 id 跨任务不唯一） */
export function isExplicitlyLinkedGoodsSibling(currentTaskId, candidateTask) {
  const currentId = String(currentTaskId || '').trim();
  const candidateId = String(candidateTask?.id || '').trim();
  if (!currentId || !candidateId || currentId === candidateId) return false;

  const candidateLinks = collectLinkedGoodsTaskIdsFromTask(candidateTask);
  if (candidateLinks.includes(currentId)) return true;

  const sourceOfCandidate = String(
    candidateTask?.result?.sourceGoodsTaskId
    || candidateTask?.result?.sourcePodTaskId
    || '',
  ).trim();
  return sourceOfCandidate === currentId;
}

/**
 * 解析与当前任务同源的 POD/主图创作兄弟任务。
 * 只接受显式关联：linkedGoodsTaskIds / sourceGoodsTaskId / 反向指向当前任务。
 * 绝不按 composite-* 或印花 id 宽松扫描（会把无关任务拼进同一行）。
 */
export async function resolveLinkedSiblingGoodsTasks(currentTask) {
  const currentId = String(currentTask?.id || '').trim();
  if (!currentId) return [];

  const linkedIds = collectLinkedGoodsTaskIdsFromTask(currentTask)
    .filter((id) => id !== currentId);

  const linkedTasks = [];
  const seen = new Set([currentId]);
  for (const id of linkedIds) {
    if (seen.has(id)) continue;
    seen.add(id);
    try {
      const res = await taskAPI.getById(id);
      const task = res?.task || res?.data || res;
      if (task && String(task.type || 'goods') === 'goods') {
        linkedTasks.push(await ensureFullGoodsTask(task));
      }
    } catch (error) {
      console.warn('[goodsExportLinked] 加载关联主图/POD 任务失败', id, error);
    }
  }

  // 反向显式关联：其它任务的 sourceGoodsTaskId / linkedGoodsTaskIds 指向当前
  let scannedTasks = [];
  try {
    const res = await taskAPI.getAll(buildHistoryTasksListQuery({}, {
      type: 'goods',
      page: 1,
      pageSize: 100,
    }));
    scannedTasks = Array.isArray(res?.tasks) ? res.tasks : [];
  } catch (error) {
    console.warn('[goodsExportLinked] 扫描 goods 任务失败', error);
  }

  const reverseMatches = [];
  for (const raw of scannedTasks) {
    const id = String(raw?.id || '').trim();
    if (!id || seen.has(id)) continue;
    if (!isSuccessLikeStatus(raw?.status) && String(raw?.status || '') !== 'processing') continue;
    if (!isExplicitlyLinkedGoodsSibling(currentId, raw)) continue;
    seen.add(id);
    reverseMatches.push(await ensureFullGoodsTask(raw));
  }

  return [...linkedTasks, ...reverseMatches].sort(
    (a, b) => taskFinishedAtMs(b) - taskFinishedAtMs(a),
  );
}

/**
 * 为兄弟任务准备导出用 goodsForms（补齐合成图 URL）。
 */
export async function loadSiblingTaskExportForms(task) {
  const forms = task?.goodsForms || task?.result?.goodsForms || [];
  if (!Array.isArray(forms) || forms.length === 0) return [];
  try {
    return await loadGoodsFormsWithCompositeImages(task.id, forms);
  } catch (error) {
    console.warn('[goodsExportLinked] 合并兄弟任务合成图失败', task?.id, error);
    return forms;
  }
}

/**
 * 将文案任务 ID 追加到上游主图/POD：result.linkedGoodsTaskIds
 */
export async function appendLinkedGoodsTaskId(sourceGoodsTaskId, linkedTaskId) {
  const sourceId = String(sourceGoodsTaskId || '').trim();
  const nextId = String(linkedTaskId || '').trim();
  if (!sourceId || !nextId || sourceId === nextId) return;
  try {
    const res = await taskAPI.getById(sourceId);
    const goodsTask = res?.task || res?.data || res;
    if (!goodsTask) return;
    const prevResult = goodsTask.result && typeof goodsTask.result === 'object'
      ? goodsTask.result
      : {};
    const prev = Array.isArray(prevResult.linkedGoodsTaskIds)
      ? prevResult.linkedGoodsTaskIds.map(String).filter(Boolean)
      : [];
    if (prev.includes(nextId)) return;
    await taskAPI.update(sourceId, {
      result: {
        ...prevResult,
        linkedGoodsTaskIds: [...prev, nextId],
      },
    });
  } catch (error) {
    console.warn('[goodsExportLinked] 回写 linkedGoodsTaskIds 失败', error);
  }
}
